import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PoweredByMeitre } from '@shared/components/common';
import { ChevronLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { checkPasswordResetToken, setNewPassword } from '@core/auth/authApi';

/**
 * Reset password page for restaurant subdomain: /reset/:token
 *
 * The backend sends a reset email with a link like:
 *   https://{restaurant}.v2.meitre.com/reset/{token}
 *
 * Flow:
 * 1. Validate token on mount → GET /{lang}/reset-password/check/{token}
 * 2. Show new password form
 * 3. Submit → POST /{lang}/reset-password/set-new-password
 * 4. Show success → redirect to home
 */
export function ResetPasswordPage() {
  const { t, i18n } = useTranslation('auth');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setValidating(false);
      return;
    }

    const lang = i18n.language || 'en';
    checkPasswordResetToken(lang, token)
      .then(() => setTokenValid(true))
      .catch(() => setTokenValid(false))
      .finally(() => setValidating(false));
  }, [token, i18n.language]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPass.length < 8) {
      setError(t('FORM_ERROR_INVALID_PASSWORD'));
      return;
    }
    if (newPass !== confirmPass) {
      setError(t('FORM_ERROR_PASSWORDS_MATCH'));
      return;
    }

    setLoading(true);
    try {
      const lang = i18n.language || 'en';
      await setNewPassword(lang, { newPass, token: token! });
      setSuccess(true);
    } catch {
      setError(t('PASSWORD_RESET_ERROR'));
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (validating) {
      return (
        <div className="flex flex-col items-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-brand mb-4" />
          <p className="text-foreground/50 text-sm">{t('PASSWORD_RESET_VALIDATING')}</p>
        </div>
      );
    }

    if (!token || !tokenValid) {
      return (
        <>
          <h1 className="text-3xl font-display mb-2 text-center">
            {t('PASSWORD_RESET_INVALID_TITLE')}
          </h1>
          <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
            {t('PASSWORD_RESET_INVALID_SUBTITLE')}
          </p>
          <div className="w-full max-w-xs">
            <Button
              className="py-6 w-full transition-all duration-200 hover:scale-[1.02]"
              onClick={() => navigate('/')}
              type="button"
            >
              {t('PASSWORD_RESET_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (success) {
      return (
        <>
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="text-green-600" size={32} />
          </div>
          <h1 className="text-3xl font-display mb-2 text-center">
            {t('PASSWORD_RESET_MESSAGE_OK')}
          </h1>
          <p className="text-foreground/50 text-sm mb-8 text-center max-w-xs">
            {t('PASSWORD_RESET_SUCCESS_SUBTITLE')}
          </p>
          <div className="w-full max-w-xs">
            <Button
              className="py-6 w-full transition-all duration-200 hover:scale-[1.02]"
              onClick={() => navigate('/')}
              type="button"
            >
              {t('PASSWORD_RESET_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <h1 className="text-3xl font-display mb-2 text-center">
          {t('PASSWORD_RESET_NEW_TITLE')}
        </h1>
        <p className="text-foreground/50 text-sm mb-8 text-center">
          {t('PASSWORD_RESET_NEW_SUBTITLE')}
        </p>

        <form
          className="w-full max-w-xs flex flex-col gap-4 mb-10"
          onSubmit={handleSubmit}
        >
          <Input
            type="password"
            placeholder={t('FORM_LABEL_PASSWORD')}
            value={newPass}
            onChange={(e) => setNewPass(e.target.value)}
            autoFocus
            className="py-6"
          />
          <Input
            type="password"
            placeholder={t('FORM_LABEL_CONFIRM_PASSWORD')}
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            className="py-6"
          />

          {error && (
            <p className="text-red-500 text-sm text-center">{error}</p>
          )}

          <Button
            type="submit"
            className="py-6 transition-all duration-200 hover:scale-[1.02]"
            disabled={loading || !newPass || !confirmPass}
          >
            {loading ? t('MAGIC_LINK_VERIFY_BUTTON') : t('PASSWORD_RESET_SET_PASSWORD')}
          </Button>
        </form>
      </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-0 lg:p-4 bg-background">
      <div className="w-full lg:max-w-md bg-card shadow-none rounded-b-2xl lg:shadow-lg lg:rounded-2xl overflow-visible lg:overflow-hidden animate-fade-in border-t border-border/30">
        <main className="px-6 pt-8 pb-10 flex flex-col items-center">
          {renderContent()}
        </main>

        {!success && !validating && tokenValid && (
          <footer className="border-t border-border px-6 py-4 flex items-center">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </footer>
        )}
      </div>

      <p className="hidden lg:block mt-6 text-xs">
        <PoweredByMeitre />
      </p>
    </div>
  );
}

export default ResetPasswordPage;
